/**
 * Job GeoJSON preparation and transformation service.
 *
 * Pure functions for cleaning, transforming, and preparing GeoJSON data
 * associated with jobs. Extracted from the API layer (api/routers/data/tables/jobs.ts).
 */

import { booleanPointInPolygon } from "@turf/boolean-point-in-polygon";
import type { Feature, FeatureCollection, Point } from "geojson";
import type { Deal } from "@domain/CRM/deal";
import type { Job } from "../types/Job";
/**
 * Applies collectBoundary flag to a job by setting boundaryChangeType to "Major"
 * and adding a note indicating the customer requested boundary collection.
 * Mutates the job object in place.
 */
export function applyCollectBoundary(job: Partial<Job>): void {
	if (job.collectBoundary) {
		job.boundaryChangeType = "Major";
		const notes = job.boundaryChangeNotes?.split("\n") || [];
		notes.push("Boundary Set by Customer.");
		job.boundaryChangeNotes = notes.join("\n");
	}
}

/**
 * Removes internal metadata properties from GeoJSON features and optionally adds a type label.
 * Returns a deep copy — does not mutate the input.
 */
export function cleanGeoJson(geojson: FeatureCollection, type?: string): FeatureCollection {
	const copy: FeatureCollection = structuredClone(geojson);
	for (const feature of copy.features) {
		if (feature.properties) {
			delete feature.properties.__uniqueJobKey;
			delete feature.properties.FilePath;
			for (let i = 1; i <= 9; i++) {
				delete feature.properties[`FolderLevel${i}`];
			}
		}
		if (type) {
			feature.properties = feature.properties || {};
			feature.properties.type = type;
		}
	}
	return copy;
}

/**
 * Renumber zone cores so that CoreID matches the zone each point belongs to.
 * Format: `${ZoneID}.${coreNumber}` (e.g., "1.1", "1.2", "2.1").
 * Returns a deep copy — does not mutate the inputs.
 */
export function renumberZoneCores(znsGeoJson: FeatureCollection, ptsGeoJson: FeatureCollection): FeatureCollection {
	const resultGeoJson = structuredClone(ptsGeoJson);

	for (const zone of znsGeoJson.features) {
		let coreNumber = 0;
		for (const point of resultGeoJson.features) {
			const pointGeom = point.geometry as Point;

			if (booleanPointInPolygon(pointGeom.coordinates, zone as Feature<any>)) {
				if (zone.properties && zone.properties.ZoneID) {
					coreNumber++;
					point.properties = point.properties || {};
					point.properties.CoreID = `${zone.properties.ZoneID}.${coreNumber}`;
				}
			}
		}
	}

	return resultGeoJson;
}

/**
 * Handles zone geometry for composite and multi-composite deals.
 * - Composite: copies boundary to zones with ZoneID=1
 * - MultiComposite: clears zones
 * Mutates job.files in place.
 */
export function handleZonesForCompositeJobs(job: Partial<Job>, deal: Deal | undefined): void {
	if (!deal || !job.files) return;

	if (deal.isComposite && job.files.boundaryGeoJSON) {
		// In Composite jobs, the zone is the same as the boundary,
		// so we're doing the copy-and-paste here to save time in map-making
		const znsGeoJson = structuredClone(job.files.boundaryGeoJSON);
		if (znsGeoJson.features) {
			znsGeoJson.features.forEach((feature: any) => {
				if (!feature.properties) feature.properties = {};
				feature.properties.ZoneID = 1;
			});
		}
		// Only set GeoJSON - SHP will be generated at upload time
		job.files.zonesGeoJSON = znsGeoJson;
	} else if (deal.isMultiComposite) {
		// In MultiComposite jobs, we still don't want their zones,
		// but we can't simply copy-and-paste the boundary
		job.files.zonesGeoJSON = undefined;
	}
}
